import React, { useEffect, useState, useCallback } from 'react';
import { DateTime } from 'luxon';
import BigNumber from 'bignumber.js';
import { useQuery } from 'react-query';
import { withStyles } from '@material-ui/core/styles';
import MuiIconButton from '@material-ui/core/IconButton';

// Import filter components
import useReportCurrencyFilters from 'hooks/use-report-currency-filters';
import ReportCurrencyColumnFilter from 'components/ReportCurrencyColumnFilter';
import ReportCurrencyActiveFilters from 'components/ReportCurrencyActiveFilters';

import MuiCheckbox from '@material-ui/core/Checkbox';
import MuiBox from '@material-ui/core/Box';
import MuiButton from '@material-ui/core/Button';
import MuiTable from '@material-ui/core/Table';
import MuiTableHead from '@material-ui/core/TableHead';
import MuiTableBody from '@material-ui/core/TableBody';
import MuiTableRow from '@material-ui/core/TableRow';
import MuiTableCell from '@material-ui/core/TableCell';
import MuiInputAdornment from '@material-ui/core/InputAdornment';
import MuiCollapse from '@material-ui/core/Collapse';
import MuiKeyboardArrowDownIcon from '@material-ui/icons/KeyboardArrowDown';
import MuiKeyboardArrowUpIcon from '@material-ui/icons/KeyboardArrowUp';
import MuiTypography from '@material-ui/core/Typography';
import MuiCircularProgress from '@material-ui/core/CircularProgress';
import MuiCallSplit from '@material-ui/icons/CallSplit';
import MuiTextField from '@material-ui/core/TextField';

import { WithTooltip } from 'utils/wrappers';
import CommentIcon from '@material-ui/icons/Comment';
import NoteAddIcon from '@material-ui/icons/NoteAdd';

import { CURRENCIES } from 'utils/constants';

import { ReportInvoices } from 'api';
const { getById: getReportInvoice } = ReportInvoices();

const styles = theme => ({
  root: {
    '& > *': {
      borderBottom: 'unset',
    },
    '& .MuiTableCell-root': {
      padding: '8px 16px',
    },
    '& .fixed-width-cell': {
      overflow: 'hidden',
      textOverflow: 'ellipsis',
      whiteSpace: 'nowrap',
    },
  },
  edit: {
    fontSize: 'medium',
  },
  actionRow: {
    display: 'flex',
  },
  actionCell: {
    marginRight: '1rem',
  },
  rightCell: {
    alignSelf: 'center',
    marginLeft: 'auto',
  },
  expandedRow: {
    backgroundColor: '#f3f3f3',
  },
  loadWrapper: {
    display: 'flex',
    justifyContent: 'center',
    margin: '1rem 0',
  },
  buttonCell: {
    whiteSpace: 'nowrap',
  },
  totalsTable: {
    marginTop: '1rem',
  },
  fileCell: {
    maxWidth: '300px',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
  },
  totalsCell: {
    border: 0,
    fontWeight: 'bold',
  },
  nullButton: {
    height: '40px',
    width: '40px',
  },
  readOnlyCell: {
    border: 0,
    padding: 0,
    margin: 0,
  },
  splitButton: {
    marginRight: theme.spacing(1),
  },
});

const determineNotesButtonContent = notes => {
  if (notes) {
    return {
      tooltip: notes,
      IconComponent: CommentIcon,
    };
  }

  return {
    tooltip: 'Save a note',
    IconComponent: NoteAddIcon,
  };
};

const EditNotesButton = ({ notes, setEditingNotesId, rowId }) => {
  const { tooltip, IconComponent } = determineNotesButtonContent(notes);

  return (
    <WithTooltip tooltip={tooltip}>
      <MuiIconButton
        onClick={() => {
          setEditingNotesId(rowId);
        }}>
        <IconComponent />
      </MuiIconButton>
    </WithTooltip>
  );
};

const Row = props => {
  const {
    classes,
    row,
    editing,
    onSplitInvoices,
    splitError,
    setEditingNotesId,
    expandedRow,
    setExpandedRow,
    setFormData,
    formData,
    children,
  } = props;

  useEffect(() => {
    if (editing) {
      setFormData({
        incoming_payment_native: row.incoming_payment_native,
        incoming_payment_usd: row.incoming_payment_usd,
      });
    }
  }, [editing, row, setFormData]);

  const isExpanded = expandedRow === row.id;

  const [selectedSubRows, setSelectedSubRows] = useState([]);
  const [exchangeRate, setExchangeRate] = useState(null);
  const [showExchangeRate, setShowExchangeRate] = useState(false);
  const [userEditedUSD, setUserEditedUSD] = useState(false);

  //This is only needed because of the way the endpoint and model were setup a long time ago.
  //it seems that for the historic reason of having unbounded query, it was nesessary to get a
  //large list and then get the items row by row to prevent crashing.
  //however the main query is bounded so it is not needed, but it would take some effort to add
  //the queires together. So we still need it here until we change the endpoint.
  const { data: dataReportInvoice = {}, isLoading } = useQuery(
    ['report-invoice', row.id, isExpanded],
    () => getReportInvoice(row.id),
    {
      enabled: !!isExpanded,
    }
  );

  // DB Data
  const dataReportCurrencyMatches = dataReportInvoice?.report_currencies || [];

  // Initialize Report Currency Filters
  const currencyFilters = useReportCurrencyFilters(dataReportCurrencyMatches, `report_currencies_${row.id}`);

  // Listeners
  const handleSelectAllClick = e => {
    if (e.target.checked) {
      const newSelecteds = currencyFilters.filteredData.map(r => r.id);
      setSelectedSubRows(newSelecteds);
      return;
    }

    setSelectedSubRows([]);
  };

  const handleSelectRowClick = (e, id) => {
    const newSelecteds = selectedSubRows.filter(rowId => e.target.checked || rowId !== id);

    if (e.target.checked) {
      newSelecteds.push(id);
    }

    setSelectedSubRows(newSelecteds);
  };

  // Calculate sum of selected native values
  const calculateSelectedNativeSum = useCallback(() => {
    const selectedRows = currencyFilters.filteredData.filter(subRow => selectedSubRows.includes(subRow.id));

    const sum = BigNumber.sum(...selectedRows.map(subRow => subRow.revenue_totals.sum_gross_revenue_native || 0));

    return sum.toString();
  }, [selectedSubRows, currencyFilters.filteredData]);

  // Update incoming payment when selection changes
  const updateIncomingPaymentFromSelection = useCallback(() => {
    if (editing && selectedSubRows.length > 0) {
      const calculatedSum = calculateSelectedNativeSum();
      setFormData(prevFormData => {
        const newFormData = {
          ...prevFormData,
          incoming_payment_native: calculatedSum,
        };
        // If user has entered USD value, recalculate exchange rate with new native value
        if (userEditedUSD && prevFormData.incoming_payment_usd) {
          const usdValue = parseFloat(prevFormData.incoming_payment_usd) || 0;
          const nativeValue = parseFloat(calculatedSum) || 0;
          if (usdValue > 0 && nativeValue > 0) {
            const calculatedRate = usdValue / nativeValue;
            setExchangeRate(calculatedRate);
            setShowExchangeRate(true);
          } else {
            setExchangeRate(null);
            setShowExchangeRate(false);
          }
        } else {
          // Reset user edited flag when auto-calculating from selection
          setUserEditedUSD(false);
        }
        return newFormData;
      });
    }
  }, [editing, selectedSubRows, calculateSelectedNativeSum, setFormData, userEditedUSD]);

  // Update incoming payment when selection changes
  useEffect(() => {
    updateIncomingPaymentFromSelection();
  }, [updateIncomingPaymentFromSelection]);

  const updateForm = ({ target: { name, value } }) => {
    setFormData({ ...formData, [name]: value });

    // Track when user manually edits USD field and calculate exchange rate
    if (name === 'incoming_payment_usd') {
      setUserEditedUSD(true);
      // Calculate exchange rate automatically when USD is entered
      const usdValue = parseFloat(value) || 0;
      const nativeValue = parseFloat(formData.incoming_payment_native) || 0;
      if (usdValue > 0 && nativeValue > 0) {
        const calculatedRate = usdValue / nativeValue;
        setExchangeRate(calculatedRate);
        setShowExchangeRate(true);
      } else {
        setExchangeRate(null);
        setShowExchangeRate(false);
      }
    }
  };

  // Handle manual update of native value
  const handleNativeValueChange = ({ target: { name, value } }) => {
    setFormData({ ...formData, [name]: value });

    // If user has edited USD, recalculate exchange rate when native value changes
    if (name === 'incoming_payment_native' && userEditedUSD) {
      const nativeValue = parseFloat(value) || 0;
      const usdValue = parseFloat(formData.incoming_payment_usd) || 0;
      if (usdValue > 0 && nativeValue > 0) {
        const calculatedRate = usdValue / nativeValue;
        setExchangeRate(calculatedRate);
        setShowExchangeRate(true);
      } else {
        setExchangeRate(null);
        setShowExchangeRate(false);
      }
    }
    // Auto-calculate USD if exchange rate is available (only if user hasn't manually edited USD)
    else if (name === 'incoming_payment_native' && exchangeRate && value && !userEditedUSD) {
      const nativeValue = parseFloat(value) || 0;
      const usdValue = nativeValue * exchangeRate;
      setFormData(prevFormData => ({
        ...prevFormData,
        incoming_payment_usd: usdValue.toString(),
      }));
    }
  };

  const handleSplitInvoicesClick = useCallback(
    () => onSplitInvoices(row, selectedSubRows),
    [onSplitInvoices, row, selectedSubRows]
  );

  const isApproved = row.is_approved === true;
  const isNotArchived = !row.is_archived;

  const invoiceTotals = {
    gross_revenue_native: BigNumber.sum(
      ...dataReportCurrencyMatches.map(rc => rc.revenue_totals.sum_gross_revenue_native)
    ),
    gross_revenue_usd: BigNumber.sum(...dataReportCurrencyMatches.map(rc => rc.revenue_totals.sum_gross_revenue_usd)),
  };

  return (
    <>
      <MuiTableRow className={classes.root}>
        <MuiTableCell component="th" scope="row" style={{ textAlign: 'center' }}>
          {row.id}
        </MuiTableCell>
        <MuiTableCell style={{ textAlign: 'center' }}>{row.dsp_name}</MuiTableCell>
        <MuiTableCell style={{ textAlign: 'center' }}>{row.native_currency_abbv}</MuiTableCell>
        <MuiTableCell style={{ textAlign: 'center' }}>{row.is_approved ? 'True' : 'False'}</MuiTableCell>
        <MuiTableCell style={{ textAlign: 'center' }}>
          {/* Exchange Rate Display */}
          <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
            {showExchangeRate && exchangeRate ? (
              <span style={{ fontSize: '14px', fontWeight: 'bold' }}>{exchangeRate.toFixed(4)}</span>
            ) : (
              <span style={{ fontSize: '14px', color: '#999' }}>
                {!row.exchange_rate_to_usd || isNaN(row.exchange_rate_to_usd)
                  ? '-'
                  : parseFloat(row.exchange_rate_to_usd).toFixed(4)}
              </span>
            )}
          </div>
        </MuiTableCell>
        <MuiTableCell style={{ textAlign: 'center' }}>
          {editing && isNotArchived ? (
            <MuiTextField
              onChange={handleNativeValueChange}
              name="incoming_payment_native"
              value={formData.incoming_payment_native || ''}
              placeholder={'0.00'}
              variant="outlined"
              size="small"
              style={{ width: '120px' }}
              inputProps={{
                style: {
                  fontSize: '14px',
                  padding: '8px 12px',
                  textAlign: 'right',
                },
              }}
              InputProps={{
                startAdornment: <MuiInputAdornment position="start">$</MuiInputAdornment>,
              }}
            />
          ) : (
            <>
              {row.incoming_payment_native ? (
                <span>
                  <MuiInputAdornment position="start">
                    {CURRENCIES[row.native_currency_abbv]}
                    {row.incoming_payment_native}
                  </MuiInputAdornment>
                </span>
              ) : null}
            </>
          )}
        </MuiTableCell>
        <MuiTableCell style={{ textAlign: 'left' }}>
          {editing && isNotArchived ? (
            <MuiTextField
              onChange={updateForm}
              name="incoming_payment_usd"
              placeholder={'0.00'}
              variant="outlined"
              size="small"
              style={{ width: '120px' }}
              inputProps={{
                style: {
                  fontSize: '14px',
                  padding: '8px 12px',
                  textAlign: 'right',
                },
              }}
              value={formData.incoming_payment_usd || ''}
              InputProps={{
                startAdornment: <MuiInputAdornment position="start">$</MuiInputAdornment>,
              }}
            />
          ) : (
            <>{row.incoming_payment_usd ? <span>${row.incoming_payment_usd}</span> : null}</>
          )}
        </MuiTableCell>
        <MuiTableCell style={{ textAlign: 'center' }}>
          <EditNotesButton notes={row.notes} setEditingNotesId={setEditingNotesId} rowId={row.id} />
        </MuiTableCell>
        <MuiTableCell style={{ textAlign: 'center' }} className={classes.buttonCell} align="center">
          <MuiBox display="flex" alignItems="center" justifyContent="right" gap={0.5}>
            {/* Row Actions Buttons */}
            <MuiBox display="flex" alignItems="center" gap={0.5}>
              {children}
            </MuiBox>
            {/* Expand Button */}
            <MuiIconButton
              aria-label="expand row"
              size="small"
              onClick={() => setExpandedRow(!isExpanded ? row.id : null)}>
              {isExpanded ? <MuiKeyboardArrowUpIcon /> : <MuiKeyboardArrowDownIcon />}
            </MuiIconButton>
          </MuiBox>
        </MuiTableCell>
      </MuiTableRow>
      <MuiTableRow className={classes.expandedRow}>
        <MuiTableCell style={{ paddingBottom: 0, paddingTop: 0 }} colSpan={9}>
          <MuiCollapse in={isExpanded} timeout="auto" unmountOnExit>
            <MuiBox
              m={1}
              style={{
                overflowX: 'auto',
                maxWidth: '100%',
                width: '100%',
              }}>
              <MuiBox m={1} className={classes.actionRow}>
                <MuiTypography variant="h6" gutterBottom component="div" className={classes.actionCell}>
                  Report Currencies
                </MuiTypography>
                <div className={classes.actionCell}>
                  <MuiButton
                    variant="contained"
                    color="primary"
                    size="small"
                    startIcon={<MuiCallSplit />}
                    onClick={handleSplitInvoicesClick}
                    className={classes.splitButton}
                    disabled={isApproved || selectedSubRows.length === 0}>
                    Split Selected to New Invoice
                  </MuiButton>
                  <span>{splitError ? 'There was an error creating the split.' : ''}</span>
                </div>
              </MuiBox>
              {isLoading ? (
                <div className={classes.loadWrapper}>
                  <MuiCircularProgress />
                </div>
              ) : !currencyFilters.filteredData || !currencyFilters.filteredData.length ? (
                <span>No Records</span>
              ) : (
                <>
                  {/* Active Filters for Report Currencies */}
                  <ReportCurrencyActiveFilters
                    filters={currencyFilters.filters}
                    columns={currencyFilters.columnConfig}
                    onClearFilter={currencyFilters.removeFilter}
                    onClearAllFilters={currencyFilters.clearAllFilters}
                  />

                  <MuiTable
                    size="small"
                    aria-label="purchases"
                    style={{
                      tableLayout: 'fixed',
                      width: '100%',
                    }}>
                    <MuiTableHead>
                      <MuiTableRow>
                        {currencyFilters.filteredData?.length && (
                          <MuiTableCell padding="checkbox" style={{ width: '50px' }}>
                            <MuiCheckbox
                              indeterminate={
                                selectedSubRows.length > 0 &&
                                selectedSubRows.length < currencyFilters.filteredData?.length
                              }
                              checked={
                                selectedSubRows.length > 0 &&
                                selectedSubRows.length === currencyFilters.filteredData?.length
                              }
                              disabled={isApproved}
                              onChange={handleSelectAllClick}
                            />
                          </MuiTableCell>
                        )}
                        <MuiTableCell style={{ width: '80px' }}>
                          ID
                          <ReportCurrencyColumnFilter
                            columnId="id"
                            columnLabel="ID"
                            filterType="text"
                            hasFilter={currencyFilters.hasColumnFilter('id')}
                            onOpenFilter={currencyFilters.openFilterPopup}
                            onApplyFilter={currencyFilters.applyFilter}
                            onClearFilter={currencyFilters.clearCurrentFilter}
                            filterValues={currencyFilters.tempFilters}
                            onFilterValueChange={currencyFilters.updateTempFilter}
                          />
                        </MuiTableCell>
                        <MuiTableCell style={{ width: '120px' }}>
                          Statement Date
                          <ReportCurrencyColumnFilter
                            columnId="statement_date"
                            columnLabel="Statement Date"
                            filterType="month"
                            hasFilter={currencyFilters.hasColumnFilter('statement_date')}
                            onOpenFilter={currencyFilters.openFilterPopup}
                            onApplyFilter={currencyFilters.applyFilter}
                            onClearFilter={currencyFilters.clearCurrentFilter}
                            filterValues={currencyFilters.tempFilters}
                            onFilterValueChange={currencyFilters.updateTempFilter}
                            options={currencyFilters.availableMonths}
                          />
                        </MuiTableCell>
                        <MuiTableCell style={{ width: '200px' }}>File</MuiTableCell>
                        <MuiTableCell style={{ width: '100px' }} align="right">
                          Native
                        </MuiTableCell>
                        <MuiTableCell style={{ width: '100px' }} align="right">
                          USD
                        </MuiTableCell>
                      </MuiTableRow>
                    </MuiTableHead>
                    <MuiTableBody>
                      {currencyFilters.filteredData.map(subRow => {
                        return (
                          <MuiTableRow key={subRow.id}>
                            <MuiTableCell padding="checkbox" style={{ width: '50px' }}>
                              <MuiCheckbox
                                checked={selectedSubRows.includes(subRow.id)}
                                disabled={isApproved}
                                onChange={e => handleSelectRowClick(e, subRow.id)}
                              />
                            </MuiTableCell>
                            <MuiTableCell style={{ width: '80px' }}>{subRow.id}</MuiTableCell>
                            <MuiTableCell style={{ width: '120px' }}>
                              {DateTime.fromISO(subRow.statement_date).toLocaleString()}
                            </MuiTableCell>
                            <MuiTableCell
                              style={{
                                width: '200px',
                                maxWidth: '200px',
                                overflow: 'hidden',
                                textOverflow: 'ellipsis',
                                whiteSpace: 'nowrap',
                              }}
                              title={subRow.source_file}>
                              {subRow.source_file}
                            </MuiTableCell>
                            <MuiTableCell style={{ width: '100px' }} align="right">
                              {subRow.revenue_totals.sum_gross_revenue_native && CURRENCIES[row.native_currency_abbv]}
                              {subRow.revenue_totals.sum_gross_revenue_native?.toString()}
                            </MuiTableCell>
                            <MuiTableCell style={{ width: '100px' }} align="right">
                              {subRow.revenue_totals.sum_gross_revenue_usd && '$'}
                              {subRow.revenue_totals.sum_gross_revenue_usd?.toString()}
                            </MuiTableCell>
                          </MuiTableRow>
                        );
                      })}
                      {dataReportCurrencyMatches?.length > 0 && (
                        <MuiTableRow>
                          <MuiTableCell />
                          <MuiTableCell />
                          <MuiTableCell />
                          <MuiTableCell className={classes.totalsCell} style={{ width: '10%' }} align="right">
                            {invoiceTotals.gross_revenue_native && CURRENCIES[row.native_currency_abbv]}
                            {invoiceTotals.gross_revenue_native?.toString()}
                          </MuiTableCell>
                          <MuiTableCell className={classes.totalsCell} style={{ width: '10%' }} align="right">
                            {invoiceTotals.gross_revenue_usd && '$'}
                            {invoiceTotals.gross_revenue_usd?.toString()}
                          </MuiTableCell>
                        </MuiTableRow>
                      )}
                    </MuiTableBody>
                  </MuiTable>
                </>
              )}
            </MuiBox>
          </MuiCollapse>
        </MuiTableCell>
      </MuiTableRow>
    </>
  );
};

export default withStyles(styles)(Row);
